import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MobileCardList, MobileCard } from "@/components/MobileCards";
import { AccessBlocked } from "@/components/AccessBlocked";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Edit2, Eye, X, AlertTriangle, FileText, Download, Trash2 } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, istToday } from "@/lib/dateFormat";
import { MultiSelectDropdown, SelectedTags } from "@/components/ui/multi-select-dropdown";
import { checkGstin } from "@/lib/gstin";

const ALL_COUNTRY_CODES = [
  {code:"+91",name:"India"},{code:"+1",name:"USA"},{code:"+1",name:"Canada"},{code:"+44",name:"UK"},{code:"+61",name:"Australia"},
  {code:"+65",name:"Singapore"},{code:"+971",name:"UAE"},{code:"+966",name:"Saudi Arabia"},{code:"+974",name:"Qatar"},{code:"+968",name:"Oman"},
  {code:"+973",name:"Bahrain"},{code:"+965",name:"Kuwait"},{code:"+81",name:"Japan"},{code:"+86",name:"China"},{code:"+82",name:"South Korea"},
  {code:"+49",name:"Germany"},{code:"+33",name:"France"},{code:"+39",name:"Italy"},{code:"+34",name:"Spain"},{code:"+31",name:"Netherlands"},
  {code:"+41",name:"Switzerland"},{code:"+46",name:"Sweden"},{code:"+47",name:"Norway"},{code:"+45",name:"Denmark"},{code:"+358",name:"Finland"},
  {code:"+48",name:"Poland"},{code:"+43",name:"Austria"},{code:"+32",name:"Belgium"},{code:"+351",name:"Portugal"},{code:"+353",name:"Ireland"},
  {code:"+7",name:"Russia"},{code:"+90",name:"Turkey"},{code:"+27",name:"South Africa"},{code:"+234",name:"Nigeria"},{code:"+254",name:"Kenya"},
  {code:"+255",name:"Tanzania"},{code:"+256",name:"Uganda"},{code:"+20",name:"Egypt"},{code:"+212",name:"Morocco"},{code:"+216",name:"Tunisia"},
  {code:"+60",name:"Malaysia"},{code:"+62",name:"Indonesia"},{code:"+63",name:"Philippines"},{code:"+66",name:"Thailand"},{code:"+84",name:"Vietnam"},
  {code:"+852",name:"Hong Kong"},{code:"+853",name:"Macau"},{code:"+886",name:"Taiwan"},{code:"+977",name:"Nepal"},{code:"+94",name:"Sri Lanka"},
  {code:"+880",name:"Bangladesh"},{code:"+95",name:"Myanmar"},{code:"+92",name:"Pakistan"},{code:"+93",name:"Afghanistan"},
  {code:"+55",name:"Brazil"},{code:"+52",name:"Mexico"},{code:"+54",name:"Argentina"},{code:"+56",name:"Chile"},{code:"+57",name:"Colombia"},
  {code:"+51",name:"Peru"},{code:"+58",name:"Venezuela"},{code:"+64",name:"New Zealand"},{code:"+679",name:"Fiji"},{code:"+675",name:"Papua New Guinea"},
  {code:"+972",name:"Israel"},{code:"+962",name:"Jordan"},{code:"+961",name:"Lebanon"},{code:"+964",name:"Iraq"},{code:"+98",name:"Iran"},
  {code:"+380",name:"Ukraine"},{code:"+36",name:"Hungary"},{code:"+420",name:"Czech Republic"},{code:"+40",name:"Romania"},{code:"+30",name:"Greece"},
];


// BATCH 3 (3.2c/3.2e) — customers whose credit days differ from their channel's.
const CreditReview = () => {
  const [data, setData] = useState({ pending_approval: [], admin_review: [] });
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const r = await api.get("/customers/credit-review"); setData(r.data); }
    catch (e) { /* not an approver — nothing to show */ }
  };
  useEffect(() => { load(); }, []);
  const rows = [...(data.pending_approval || []), ...(data.admin_review || [])];
  if (rows.length === 0) return null;
  const act = async (id, approve) => {
    try {
      if (approve) await api.post(`/customers/${id}/credit-terms/approve`);
      else {
        const reason = window.prompt("Reason for rejecting these credit days:");
        if (!reason) return;
        await api.post(`/customers/${id}/credit-terms/reject`, { reason, reset_to_channel: true });
      }
      toast.success(approve ? "Credit days approved" : "Credit days rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="mb-4 border border-amber-300 bg-amber-50 rounded-sm" data-testid="credit-review-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2" data-testid="credit-review-toggle">
        <span className="text-xs font-semibold text-amber-900">Admin review — credit days ({rows.length})</span>
        <span className="text-[10px] text-amber-700">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {rows.map(r => (
            <div key={r._id} className="bg-white border border-amber-200 rounded-sm p-2 flex items-center justify-between gap-3" data-testid={`credit-review-${r._id}`}>
              <div className="text-[11px]">
                <p className="font-semibold text-zinc-900">{r.company_name || r.name}
                  <span className="ml-2 text-[9px] uppercase text-zinc-400">{r.status}</span></p>
                <p className="text-zinc-600">{r.channel_name}: channel {r.channel_credit_days} days · asked
                  {" "}{r.pending_credit_days ?? r.credit_days} days{r.reason ? ` · ${r.reason}` : ""}</p>
                {r.rejection_reason ? <p className="text-red-600">Rejected: {r.rejection_reason}</p> : null}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button className="h-7 text-[10px] rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={() => act(r._id, true)} data-testid={`credit-approve-${r._id}`}>Approve</Button>
                <Button variant="outline" className="h-7 text-[10px] rounded-sm border-red-300 text-red-600" onClick={() => act(r._id, false)} data-testid={`credit-reject-${r._id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CustomersPage() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdminRole = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const canCreateCustomer = isAdminRole || perms.some(p => ["customers.create", "admin.full_access"].includes(p));
  const canEditCustomer = isAdminRole || perms.some(p => ["customers.edit_own", "customers.edit_all", "admin.full_access"].includes(p));
  const canCreatePI = isAdminRole || perms.some(p => ["orders.create", "admin.full_access"].includes(p));
  const canDeleteCustomer = isAdminRole || perms.includes("admin.full_access");
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const CUSTOMER_TYPES = getValues("customer_types");
  const STATES = getValues("indian_states");
  const CUSTOMER_STATUSES = getValues("customer_statuses");
  const PLATFORMS = getValues("qcom_platforms");
  const TRADE_FORMATS = getValues("channel_types");
  const ONBOARDING_STATUSES = getValues("platform_onboarding_statuses");
  const [customers, setCustomers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);
  const [search, setSearch] = useState("");
  const [custPage, setCustPage] = useState(1);
  const CUST_PER_PAGE = 30;
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilterCust, setStatusFilterCust] = useState("");
  const [removed, setRemoved] = useState([]);           // PASS 2 (B7)
  const [showRemoved, setShowRemoved] = useState(false);
  const [channelFilterCust, setChannelFilterCust] = useState("");
  const [tradeFormatFilter, setTradeFormatFilter] = useState([]);
  const [channelMultiFilter, setChannelMultiFilter] = useState([]);
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  // SS-specific state
  const [ssOnboarding, setSsOnboarding] = useState([]);
  const [ssStock, setSsStock] = useState([]);
  const [ssReorderSettings, setSsReorderSettings] = useState({ auto_reorder_enabled: true, reorder_weeks_threshold: 2 });
  // FOLLOW-UP 3 (2c) — the company-wide switch
  const [autoReorderGlobalOn, setAutoReorderGlobalOn] = useState(true);
  // PI state
  const [showPIDialog, setShowPIDialog] = useState(false);
  const [piCustomer, setPiCustomer] = useState(null);
  const [piLines, setPiLines] = useState([{ sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }]);
  const [piTerms, setPiTerms] = useState("As agreed");
  const [piNotes, setPiNotes] = useState("");
  const [piGenerating, setPiGenerating] = useState(false);
  const [piHistory, setPiHistory] = useState([]);
  const [allSKUs, setAllSKUs] = useState([]);
  const [piChannel, setPiChannel] = useState("");
  const [piChannels, setPiChannels] = useState([]);
  const [form, setForm] = useState({
    name: "", company_name: "", type: "", phones: [""], email: "",
    billing_address: { line1: "", city: "", state: "", pincode: "" },
    delivery_same_as_billing: true,
    delivery_addresses: [],
    gst_number: "", pan_number: "", state: "", credit_limit: 0, credit_days: 0,
    gst_not_available: false,
    max_discount: "", channel_id: "", status: "Active"
  });
  const [phoneDuplicates, setPhoneDuplicates] = useState({});
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [ccSearch, setCcSearch] = useState({});

  // Detect if customer is Indian (based on first phone's country code)
  const isIndianCustomer = () => {
    const firstPhone = (form.phones || [""])[0] || "";
    return !firstPhone.startsWith("+") || firstPhone.startsWith("+91");
  };

  // Phone duplicate check
  const checkPhoneDuplicate = async (phone, index) => {
    if (!phone || phone.length < 5) { setPhoneDuplicates(prev => { const n = {...prev}; delete n[index]; return n; }); return; }
    try {
      const { data } = await api.get("/customers/check-phone", { params: { phone } });
      if (data.exists && (!editingCustomer || data.customer?.id !== editingCustomer._id)) {
        setPhoneDuplicates(prev => ({...prev, [index]: data.customer}));
      } else {
        setPhoneDuplicates(prev => { const n = {...prev}; delete n[index]; return n; });
      }
    } catch (err) { console.error("CustomersPage error:", err); }
  };

  // Company name autocomplete
  const searchCompanyName = async (q) => {
    if (!q || q.length < 2) { setCompanySuggestions([]); setShowCompanySuggestions(false); return; }
    try {
      const { data } = await api.get("/customers/autocomplete", { params: { q } });
      setCompanySuggestions(data || []);
      setShowCompanySuggestions(data.length > 0);
    } catch { setCompanySuggestions([]); }
  };

  // Add phone number
  const addPhone = () => setForm({...form, phones: [...form.phones, ""]});
  const removePhone = (i) => { const p = [...form.phones]; p.splice(i, 1); setForm({...form, phones: p}); };
  const updatePhone = (i, val) => { const p = [...form.phones]; p[i] = val; setForm({...form, phones: p}); checkPhoneDuplicate(val, i); };

  const loadData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilterCust) params.status = statusFilterCust;
      if (channelFilterCust) params.channel_id = channelFilterCust;
      const [custRes, chRes] = await Promise.all([
        api.get("/customers", { params }),
        api.get("/channels")
      ]);
      setCustomers(custRes.data);
      setChannels(chRes.data);
      setBlocked(null);
    } catch (err) {
      // FX3 A4/C11 — a zero-permission user must see an access state, not the master
      if ([401, 403].includes(err.response?.status)) { setCustomers([]); setBlocked(err.response?.data?.detail || "No access"); }
      else console.error("API get /customers error:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [search, typeFilter, statusFilterCust, channelFilterCust]);

  const openCreate = () => {
    setEditingCustomer(null);
    setForm({ name: "", company_name: "", type: getValues("customer_types")[0] || "", phones: [""], email: "", billing_address: { line1: "", city: "", state: "", pincode: "" }, delivery_same_as_billing: true, delivery_addresses: [], gst_number: "", pan_number: "", state: "", credit_limit: 0, credit_days: 0, max_discount: "", channel_id: "", status: "Active", gst_not_available: false });
    setPhoneDuplicates({});
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditingCustomer(c);
    const hasDAs = (c.delivery_addresses || []).length > 0;
    setForm({
      name: c.name, company_name: c.company_name || "", type: c.type || getValues("customer_types")[0] || "",
      phones: c.phones?.length ? c.phones : [""], email: c.email || "",
      billing_address: c.billing_address || { line1: "", city: "", state: "", pincode: "" },
      delivery_same_as_billing: c.delivery_same_as_billing ?? !hasDAs,
      delivery_addresses: c.delivery_addresses || [],
      gst_number: c.gst_number || "", pan_number: c.pan_number || "", state: c.state || "",
      gst_not_available: c.gst_not_available || false,
      credit_limit: c.credit_limit || 0, credit_days: c.credit_days || 0,
      max_discount: c.max_discount ?? "", channel_id: c.channel_id || "", status: c.status || "Active",
      platform_vendor_names: c.platform_vendor_names || []
    });
    setPhoneDuplicates({});
    setShowDialog(true);
    // Load SS-specific data
    if (c.type === "Qcom Super Stockist") {
      api.get(`/ss-inventory/onboarding/${c._id}`).then(r => setSsOnboarding(r.data)).catch(() => setSsOnboarding([]));
      api.get(`/ss-inventory/stock/${c._id}`).then(r => setSsStock(r.data)).catch(() => setSsStock([]));
      api.get(`/ss-inventory/reorder-status/${c._id}`).then(r => setSsReorderSettings({
        auto_reorder_enabled: r.data.auto_reorder_enabled ?? true,
        _global: setAutoReorderGlobalOn(r.data.auto_reorder_global_on !== false) || undefined,
        reorder_weeks_threshold: r.data.reorder_weeks_threshold ?? 2,
      })).catch(err => console.error("CustomersPage error:", err));
    } else {
      setSsOnboarding([]);
      setSsStock([]);
      setSsReorderSettings({ auto_reorder_enabled: true, reorder_weeks_threshold: 2 });
    }
  };

  // Delivery address helpers
  const addDeliveryAddress = () => setForm({...form, delivery_addresses: [...form.delivery_addresses, { label: `Delivery ${form.delivery_addresses.length + 1}`, line1: "", city: "", state: "", pincode: "", contact_person: "", contact_phone: "", gst_number: "" }]});
  const removeDeliveryAddress = (i) => { const d = [...form.delivery_addresses]; d.splice(i, 1); setForm({...form, delivery_addresses: d}); };
  const updateDeliveryAddress = (i, field, value) => { const d = [...form.delivery_addresses]; d[i] = {...d[i], [field]: value}; setForm({...form, delivery_addresses: d}); };

  // Pincode auto-fetch for Indian postal codes
  const fetchPincode = async (pincode, updateFn) => {
    if (!pincode || pincode.length !== 6 || !/^\d{6}$/.test(pincode)) return;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      if (data?.[0]?.Status === "Success" && data[0].PostOffice?.length > 0) {
        const po = data[0].PostOffice[0];
        updateFn(po.Division || po.District || "", po.State || "");
      }
    } catch (err) { console.error("res error:", err); }
  };

  // PI functions
  const openPIDialog = async (c) => {
    if (!c.gst_number && !c.gst_not_available) { toast.error("Please add GST number to this customer before generating PI"); return; }
    if (!["Active", "Prospect"].includes(c.status)) { toast.error("PI can only be generated for Active or Prospect customers"); return; }
    setPiCustomer(c);
    setPiLines([{ sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }]);
    setPiTerms("As agreed"); setPiNotes(""); setPiChannel(c.channel_id || "");
    if (allSKUs.length === 0) { try { const { data } = await api.get("/catalogue/skus"); setAllSKUs(data); } catch (err) { console.error("CustomersPage error:", err); } }
    if (piChannels.length === 0) { try { const { data } = await api.get("/admin/channels"); setPiChannels(data); } catch (err) { console.error("API get /catalogue/skus error:", err); } }
    api.get(`/customers/${c._id}/proforma-invoices`).then(r => setPiHistory(r.data)).catch(() => setPiHistory([]));
    setShowPIDialog(true);
  };

  const generatePI = async () => {
    const activeLines = piLines.filter(l => l.sku_id && l.quantity > 0);
    if (activeLines.length === 0) { toast.error("Add at least one SKU"); return; }
    setPiGenerating(true);
    try {
      const response = await api.post(`/customers/${piCustomer._id}/proforma-invoice`, {
        customer_id: piCustomer._id, line_items: activeLines, payment_terms: piTerms, notes: piNotes,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `PI-${piCustomer.name}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Proforma Invoice generated and downloaded");
      api.get(`/customers/${piCustomer._id}/proforma-invoices`).then(r => setPiHistory(r.data)).catch(err => console.error("CustomersPage error:", err));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to generate PI"); }
    finally { setPiGenerating(false); }
  };

  const handleSave = async () => {
    // PASS 2 (B5): a customer must carry a sales channel — that channel decides the payment type,
    // the credit days and the overdue rules, so the form refuses to save without one.
    if (!form.channel_id) { toast.error("Sales Channel is required"); return; }
    // PASS 2 (B6): the GSTIN rule is checked here too, so the message arrives before the save.
    if (!form.gst_not_available) {
      const g = checkGstin(form.gst_number, form.billing_address?.state || form.state || "");
      if (!g.valid) { toast.error(g.error); return; }
    }
    try {
      const payload = {
        ...form,
        max_discount: form.max_discount === "" ? null : parseFloat(form.max_discount),
        // If delivery same as billing, store billing as the single delivery address
        shipping_addresses: form.delivery_same_as_billing
          ? [{ ...form.billing_address, label: "Billing Address", contact_person: "", contact_phone: "" }]
          : form.delivery_addresses,
      };
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer._id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer created");
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const getChannelName = (id) => channels.find(c => c._id === id)?.name || '-';

  const filteredChannelsCust = tradeFormatFilter.length > 0 ? channels.filter(c => tradeFormatFilter.includes(c.type)) : channels;
  const channelOptionsCust = filteredChannelsCust.filter(c => c.status === "active").map(c => ({ value: c._id, label: c.name, group: c.type || "Other" }));

  const toggleSort = (col) => { if (sortCol === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); } else { setSortCol(col); setSortDir("asc"); } };
  const SH = ({ col, children }) => (<th className="cursor-pointer select-none hover:bg-zinc-50" onClick={() => toggleSort(col)}>{children} {sortCol === col && <span className="text-[9px]">{sortDir === "asc" ? "\u2191" : "\u2193"}</span>}</th>);
  const sortedCustomers = [...customers].filter(c => {
    if (channelMultiFilter.length > 0 && !channelMultiFilter.includes(c.channel_id)) return false;
    return true;
  }).sort((a, b) => {
    if (!sortCol) return 0;
    const map = { company: c => (c.company_name || c.name || "").toLowerCase(), contact: c => (c.company_name ? c.name : "").toLowerCase(), type: c => (c.type || "").toLowerCase(), state: c => (c.state || "").toLowerCase(), channel: c => getChannelName(c.channel_id).toLowerCase() };
    const fn = map[sortCol]; if (!fn) return 0;
    const va = fn(a), vb = fn(b);
    return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
  });
  const fmt = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

  const selectedChannel = (channels || []).find(c => String(c._id) === String(form.channel_id));
  const channelCreditDays = selectedChannel && selectedChannel.payment_type === "Credit"
    ? Number(selectedChannel.credit_days || 0) : null;

  return (
    <div data-testid="customers-page">
      <CreditReview />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Customers</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your customer base</p>
        </div>
        <div className="flex gap-2">
          {(isAdminRole || perms.includes("customers.export")) && <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-customers-btn" onClick={() => {
            const params = new URLSearchParams();
            if (typeFilter) params.set("type", typeFilter);
            if (statusFilterCust) params.set("status", statusFilterCust);
            if (channelFilterCust) params.set("channel_id", channelFilterCust);
            api.get(`/customers/export?${params.toString()}`, { responseType: "blob" }).then(r => {
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement("a"); a.href = url; a.download = `Customers_${istToday()}.xlsx`; a.click();
              toast.success("Customers exported");
            }).catch(err => toast.error(err.response?.status === 400 ? "No data to export" : "Export failed"));
          }}><Download size={14} /> Export Excel</Button>}
          {canCreateCustomer && <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-customer-button">
            <Plus size={14} /> Add Customer
          </Button>}
          {/* PASS 2 (B7): an Admin can see what was removed and bring it back */}
          {isAdminRole && <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="removed-customers-btn"
            onClick={async () => {
              if (showRemoved) { setShowRemoved(false); return; }
              try {
                const { data } = await api.get("/customers-removed");
                setRemoved(data); setShowRemoved(true);
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}><Trash2 size={14} /> Removed ({removed.length || 0})</Button>}
        </div>
      </div>

      {showRemoved && (
        <div className="mb-4 border border-zinc-200 rounded-sm p-3" data-testid="removed-customers-panel">
          <p className="text-xs font-semibold text-zinc-700 mb-2">Removed customers — an Admin can restore one as Active or Inactive</p>
          {removed.length === 0 ? <p className="text-[11px] text-zinc-400" data-testid="removed-customers-empty">Nothing has been removed.</p> : (
            <table className="data-table w-full">
              <thead><tr><th>Customer</th><th>Type</th><th>Removed on</th><th className="num">Records</th><th>Restore as</th></tr></thead>
              <tbody>
                {removed.map(r => (
                  <tr key={r._id} data-testid={`removed-customer-${r._id}`}>
                    <td className="text-xs">{r.name}</td>
                    <td className="text-[11px]">{r.type}</td>
                    <td className="text-[11px]">{fmtDate(r.deleted_at)}</td>
                    <td className="num text-[11px]">{r.records}</td>
                    <td className="whitespace-nowrap">
                      {["Active", "Inactive"].map(st => (
                        <Button key={st} size="sm" variant="outline" className="h-7 text-[10px] rounded-sm mr-1"
                          data-testid={`restore-customer-${st.toLowerCase()}-${r._id}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/customers/${r._id}/restore`, { status: st });
                              toast.success(data.message);
                              setRemoved(removed.filter(x => x._id !== r._id));
                              loadData();
                            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                          }}>{st}</Button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search customers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="customers-search-input" />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Customer Type</span>
          <Select value={typeFilter || "all"} onValueChange={v => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 h-8 text-xs rounded-sm" data-testid="cust-type-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{CUSTOMER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Status</span>
          <Select value={statusFilterCust || "all"} onValueChange={v => setStatusFilterCust(v === "all" ? "" : v)}>
            <SelectTrigger className="w-28 h-8 text-xs rounded-sm" data-testid="cust-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{CUSTOMER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Trade Format</span>
          <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tradeFormatFilter} onChange={v => { setTradeFormatFilter(v); setChannelMultiFilter(prev => prev.filter(id => filteredChannelsCust.some(c => c._id === id))); }} />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Sales Channel</span>
          <MultiSelectDropdown label="Sales Channel" options={channelOptionsCust} selected={channelMultiFilter} onChange={setChannelMultiFilter} groupBy="group" />
        </div>
        {(typeFilter || statusFilterCust || channelFilterCust || tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
          <Button variant="ghost" className="h-8 text-[10px] text-blue-700" onClick={() => { setTypeFilter(""); setStatusFilterCust(""); setChannelFilterCust(""); setTradeFormatFilter([]); setChannelMultiFilter([]); }}>Clear Filters</Button>
        )}
      </div>
      {(tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
        <div className="flex flex-wrap gap-1 mb-2">
          <SelectedTags selected={tradeFormatFilter} options={TRADE_FORMATS} onRemove={v => setTradeFormatFilter(tradeFormatFilter.filter(x => x !== v))} />
          <SelectedTags selected={channelMultiFilter} options={channelOptionsCust} onRemove={v => setChannelMultiFilter(channelMultiFilter.filter(x => x !== v))} />
        </div>
      )}

      {blocked && <AccessBlocked detail={blocked} testid="customers-access-blocked" />}

      {!blocked && <>
      {/* FX3 C12 — mobile card view: credit limit, status and actions stay reachable */}
      <MobileCardList testid="customer-cards">
        {loading ? <p className="text-center py-8 text-zinc-400 text-xs">Loading...</p> :
         sortedCustomers.length === 0 ? <p className="text-center py-8 text-zinc-400 text-xs">No customers found</p> :
         sortedCustomers.slice((custPage - 1) * CUST_PER_PAGE, custPage * CUST_PER_PAGE).map(c => (
          <MobileCard
            key={c._id}
            testid={`customer-card-${c._id}`}
            title={c.company_name || c.name}
            subtitle={c.company_name ? c.name : null}
            amount={fmt(c.credit_limit)}
            badge={<span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-sm ${c.status === 'Active' ? 'status-active' : c.status === 'Inactive' ? 'bg-zinc-200 text-zinc-700' : c.status === 'On Hold' ? 'status-warning' : 'status-danger'}`}>{c.status}{c.pending_credit_days ? <span className="block text-[9px] text-amber-700">credit days {c.pending_credit_days} awaiting approval</span> : null}</span>}
            rows={[
              { label: "Customer type", value: c.type },
              { label: "State", value: c.state },
              { label: "Channel", value: getChannelName(c.channel_id) },
            ]}
            actions={<>
              {canEditCustomer && <button onClick={() => openEdit(c)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`customer-card-edit-${c._id}`}><Edit2 size={14} className="text-zinc-500" /></button>}
              {canCreatePI && <button onClick={() => openPIDialog(c)} className="p-1 hover:bg-blue-50 rounded-sm" title="Create Proforma Invoice" data-testid={`customer-card-pi-${c._id}`}><FileText size={14} className="text-blue-500" /></button>}
            </>}
          />
        ))}
      </MobileCardList>

      <div className="hidden md:block border border-zinc-200 overflow-x-auto">
        <table className="data-table w-full" data-testid="customers-table">
          <thead>
            <tr><SH col="company">Company Name</SH><SH col="contact">Contact Person</SH><SH col="type">Customer Type</SH><SH col="state">State</SH><SH col="channel">Channel</SH><th className="num">Credit Limit</th><th>Status</th><th className="w-16">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            sortedCustomers.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No customers found</td></tr> :
            sortedCustomers.slice((custPage - 1) * CUST_PER_PAGE, custPage * CUST_PER_PAGE).map(c => (
              <tr key={c._id} data-testid={`customer-row-${c._id}`}>
                <td className="font-medium text-zinc-900">{c.company_name || c.name}</td>
                <td className="text-zinc-500 text-xs">{c.company_name ? c.name : '-'}</td>
                <td><span className="px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm">{c.type}</span></td>
                <td>{c.state || '-'}</td>
                <td>{getChannelName(c.channel_id)}</td>
                <td className="num font-mono">{fmt(c.credit_limit)}</td>
                <td><span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm ${c.status === 'Active' ? 'status-active' : c.status === 'Inactive' ? 'bg-zinc-200 text-zinc-700' : c.status === 'On Hold' ? 'status-warning' : 'status-danger'}`}>{c.status}{c.pending_credit_days ? <span className="block text-[9px] text-amber-700">credit days {c.pending_credit_days} awaiting approval</span> : null}</span></td>
                <td>
                  <div className="flex gap-1">
                    {canEditCustomer && <button onClick={() => openEdit(c)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`customer-edit-${c._id}`}><Edit2 size={13} className="text-zinc-500" /></button>}
                    {canCreatePI && <button onClick={() => openPIDialog(c)} className="p-1 hover:bg-blue-50 rounded-sm" title="Create Proforma Invoice" data-testid={`pi-btn-${c._id}`}><FileText size={13} className="text-blue-500" /></button>}
                    {canDeleteCustomer && <button onClick={async () => {
                      const name = window.prompt(`Delete "${c.company_name || c.name}"?\n\nType the name exactly to confirm:`);
                      if (name !== (c.company_name || c.name)) { if (name !== null) toast.error("Name doesn't match"); return; }
                      try { await api.delete(`/customers/${c._id}`); toast.success("Customer deleted"); loadData(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }} className="p-1 hover:bg-red-50 rounded-sm" title="Delete customer"><Trash2 size={13} className="text-red-400" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {/* Pagination */}
      {customers.length > CUST_PER_PAGE && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-zinc-500">Showing {(custPage - 1) * CUST_PER_PAGE + 1}–{Math.min(custPage * CUST_PER_PAGE, customers.length)} of {customers.length}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={custPage <= 1} onClick={() => setCustPage(custPage - 1)}>Previous</Button>
            <span className="text-xs text-zinc-500">Page {custPage} of {Math.ceil(customers.length / CUST_PER_PAGE)}</span>
            <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={custPage >= Math.ceil(customers.length / CUST_PER_PAGE)} onClick={() => setCustPage(custPage + 1)}>Next</Button>
          </div>
        </div>
      )}



      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingCustomer ? "Edit Customer" : "Create Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Person Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="customer-name-input" placeholder="e.g. Nikhil" /></div>
              <div className="relative">
                <Label className="text-xs">Company Name</Label>
                <Input value={form.company_name} onChange={(e) => { setForm({...form, company_name: e.target.value}); searchCompanyName(e.target.value); }}
                  onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 200)}
                  onFocus={() => companySuggestions.length > 0 && setShowCompanySuggestions(true)}
                  className="mt-1 rounded-sm" data-testid="customer-company-input" placeholder="Start typing to check for duplicates..." />
                {showCompanySuggestions && companySuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-amber-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={12} /> Similar customers found — check for duplicates</div>
                    {companySuggestions.map(s => (
                      <div key={s.id} className="px-3 py-2 hover:bg-amber-50 border-b border-zinc-50 text-sm">
                        <span className="font-medium text-zinc-900">{s.name}</span>
                        {s.company && <span className="text-zinc-500 ml-1">({s.company})</span>}
                        <span className="text-zinc-400 text-xs ml-2">{s.type} &middot; {s.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CUSTOMER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">State</Label>
                {isIndianCustomer() ? (
                  <Select value={form.state || "none"} onValueChange={(v) => setForm({...form, state: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="customer-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select State --</SelectItem>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} placeholder="State / Province" className="mt-1 rounded-sm" data-testid="customer-state-select" />
                )}
              </div>
            </div>
            <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" data-testid="customer-email-input" /></div>
            {/* Multi-phone with country code + duplicate detection */}
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs">Phone Numbers</Label><Button variant="ghost" onClick={addPhone} className="h-6 text-xs px-2"><Plus size={12} /> Add Phone</Button></div>
              {form.phones.map((ph, i) => {
                const currentCode = ph.startsWith("+") ? ph.split(" ")[0] : "+91";
                const searchTerm = (ccSearch[i] || "").toLowerCase();
                const filteredCodes = searchTerm
                  ? ALL_COUNTRY_CODES.filter(c => c.code.includes(searchTerm) || c.name.toLowerCase().includes(searchTerm))
                  : ALL_COUNTRY_CODES;
                return (
                <div key={i} className="mt-1">
                  <div className="flex items-center gap-1">
                    <div className="relative shrink-0">
                      <Select value={currentCode} onValueChange={(v) => { const num = ph.replace(/^\+\d+\s?/, ""); updatePhone(i, v + " " + num); setCcSearch(prev => ({...prev, [i]: ""})); }}>
                        <SelectTrigger className="w-24 h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <div className="px-2 py-1 sticky top-0 bg-white"><Input value={ccSearch[i] || ""} onChange={(e) => setCcSearch(prev => ({...prev, [i]: e.target.value}))} placeholder="Search country..." className="h-7 text-xs rounded-sm" autoFocus /></div>
                          {filteredCodes.map((c, ci) => <SelectItem key={`${c.code}-${c.name}-${ci}`} value={c.code}>{c.code} {c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input value={ph.replace(/^\+\d+\s?/, "")} onChange={(e) => { updatePhone(i, currentCode + " " + e.target.value); }}
                      placeholder="Phone number" className={`rounded-sm flex-1 ${phoneDuplicates[i] ? 'border-red-400 bg-red-50' : ''}`} data-testid={`customer-phone-${i}`} />
                    {form.phones.length > 1 && <Button variant="ghost" size="icon" onClick={() => removePhone(i)} className="h-8 w-8 shrink-0 text-zinc-400"><X size={14} /></Button>}
                  </div>
                  {phoneDuplicates[i] && (
                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-1">
                      <AlertTriangle size={12} /> Duplicate: This phone belongs to <span className="font-semibold">{phoneDuplicates[i].name}</span> ({phoneDuplicates[i].company})
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            <div><Label className="text-xs">Billing Address</Label>
              <Input value={form.billing_address.line1} onChange={(e) => setForm({...form, billing_address: {...form.billing_address, line1: e.target.value}})} placeholder="Street address" className="mt-1 rounded-sm" data-testid="customer-address-input" />
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Input value={form.billing_address.city || ""} onChange={(e) => setForm({...form, billing_address: {...form.billing_address, city: e.target.value}})} placeholder="City" className="rounded-sm" />
                {isIndianCustomer() ? (
                  <Select value={form.billing_address.state || "none"} onValueChange={(v) => setForm({...form, billing_address: {...form.billing_address, state: v === "none" ? "" : v}})}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- State --</SelectItem>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form.billing_address.state || ""} onChange={(e) => setForm({...form, billing_address: {...form.billing_address, state: e.target.value}})} placeholder="State / Province" className="rounded-sm" />
                )}
                <Input value={form.billing_address.pincode || ""} onChange={(e) => { const pin = e.target.value; setForm({...form, billing_address: {...form.billing_address, pincode: pin}}); fetchPincode(pin, (city, state) => setForm(prev => ({...prev, billing_address: {...prev.billing_address, city, state}, state}))); }} placeholder="PIN Code (6 digits)" className="rounded-sm" maxLength={6} />
              </div>
            </div>

            {/* Delivery Address Section */}
            <div className="border border-zinc-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input type="checkbox" checked={form.delivery_same_as_billing} onChange={(e) => setForm({...form, delivery_same_as_billing: e.target.checked})} className="rounded" data-testid="delivery-same-checkbox" />
                <span className="text-sm font-medium text-zinc-700">Delivery address same as Billing address</span>
              </label>

              {!form.delivery_same_as_billing && (
                <div className="space-y-3">
                  {form.delivery_addresses.map((da, i) => (
                    <div key={i} className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid={`delivery-address-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Input value={da.label} onChange={(e) => updateDeliveryAddress(i, "label", e.target.value)} className="rounded-sm h-7 text-xs font-semibold w-48" placeholder="e.g. Warehouse 1, Head Office" />
                        <Button variant="ghost" size="icon" onClick={() => removeDeliveryAddress(i)} className="h-7 w-7 text-zinc-400 hover:text-red-500"><X size={14} /></Button>
                      </div>
                      <Input value={da.line1} onChange={(e) => updateDeliveryAddress(i, "line1", e.target.value)} placeholder="Street address / Locality" className="rounded-sm mb-1" />
                      <div className="grid grid-cols-3 gap-2 mb-1">
                        <Input value={da.city || ""} onChange={(e) => updateDeliveryAddress(i, "city", e.target.value)} placeholder="City" className="rounded-sm" />
                        {isIndianCustomer() ? (
                          <Select value={da.state || "none"} onValueChange={(v) => updateDeliveryAddress(i, "state", v === "none" ? "" : v)}>
                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent><SelectItem value="none">-- State --</SelectItem>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input value={da.state || ""} onChange={(e) => updateDeliveryAddress(i, "state", e.target.value)} placeholder="State / Province" className="rounded-sm" />
                        )}
                        <Input value={da.pincode || ""} onChange={(e) => { const pin = e.target.value; updateDeliveryAddress(i, "pincode", pin); fetchPincode(pin, (city, state) => { updateDeliveryAddress(i, "city", city); updateDeliveryAddress(i, "state", state); }); }} placeholder="PIN Code (6 digits)" className="rounded-sm" maxLength={6} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={da.contact_person || ""} onChange={(e) => updateDeliveryAddress(i, "contact_person", e.target.value)} placeholder="Contact person at this address" className="rounded-sm" />
                        <Input value={da.contact_phone || ""} onChange={(e) => updateDeliveryAddress(i, "contact_phone", e.target.value)} placeholder="Contact phone" className="rounded-sm" />
                      </div>
                      <div className="mt-1">
                        <Input value={da.gst_number || ""} onChange={(e) => updateDeliveryAddress(i, "gst_number", e.target.value.toUpperCase().slice(0, 15))} placeholder="GST Number for this address (e.g. 29AAACK0354L1ZD)" className="rounded-sm" maxLength={15} />
                      </div>
                    </div>
                  ))}
                  {form.delivery_addresses.length < 20 ? (
                    <Button variant="outline" onClick={addDeliveryAddress} className="rounded-sm text-xs w-full" data-testid="add-delivery-address"><Plus size={12} /> Add Delivery Address ({form.delivery_addresses.length}/20)</Button>
                  ) : (
                    <p className="text-[10px] text-zinc-400 text-center py-1">Maximum 20 delivery addresses reached</p>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">GST Number</Label><Input value={form.gst_number} onChange={(e) => setForm({...form, gst_number: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15)})} className="mt-1 rounded-sm" data-testid="customer-gst-input" disabled={form.gst_not_available} />
              {/* PASS 2 (B6): the GSTIN rule, checked as it is typed */}
              {!form.gst_not_available && form.gst_number ? (() => {
                const g = checkGstin(form.gst_number, form.billing_address?.state || form.state || "");
                return g.valid
                  ? <p className="text-[10px] text-green-700 mt-1" data-testid="customer-gst-ok">GSTIN looks right — {g.stateFromGstin}</p>
                  : <p className="text-[10px] text-red-600 mt-1" data-testid="customer-gst-error">{g.error}</p>;
              })() : null}
              <label className="flex items-center gap-1.5 mt-1 text-xs text-zinc-500"><input type="checkbox" checked={form.gst_not_available} onChange={e => setForm({...form, gst_not_available: e.target.checked, gst_number: e.target.checked ? "" : form.gst_number})} className="rounded border-zinc-300" data-testid="customer-gst-na-checkbox" />No GST number available</label></div>
              <div><Label className="text-xs">PAN Number</Label><Input value={form.pan_number} onChange={(e) => setForm({...form, pan_number: e.target.value})} className="mt-1 rounded-sm" data-testid="customer-pan-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({...form, credit_limit: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="customer-credit-input" /></div>
              <div><Label className="text-xs">Credit Days</Label><Input type="number" value={form.credit_days} onChange={(e) => setForm({...form, credit_days: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="customer-credit-days" />
                {channelCreditDays !== null && Number(form.credit_days || 0) !== channelCreditDays && (
                  <div className="mt-2 border border-amber-300 bg-amber-50 rounded-sm p-2">
                    <p className="text-[10px] text-amber-900 font-semibold">This channel allows {channelCreditDays} credit days. An Admin has to approve {form.credit_days || 0} days.</p>
                    <Input value={form.credit_days_reason || ""} onChange={(e) => setForm({...form, credit_days_reason: e.target.value})}
                           className="mt-1 h-7 text-xs rounded-sm" placeholder="Reason (required)" data-testid="customer-credit-days-reason" />
                  </div>
                )}</div>
              <div><Label className="text-xs">Max Discount %</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({...form, max_discount: e.target.value})} placeholder="Optional" className="mt-1 rounded-sm" /></div>
            </div>
            <div>
              <Label className="text-xs">Sales Channel *</Label>
              {/* PASS 2 (B5): compulsory, and picking it fills the channel's own credit days */}
              <Select value={form.channel_id} onValueChange={(v) => {
                const ch = (channels || []).find(c => String(c._id) === String(v));
                const days = ch && ch.payment_type === "Credit" ? Number(ch.credit_days || 0) : 0;
                setForm({ ...form, channel_id: v, credit_days: days, credit_days_reason: "" });
                if (ch) toast.info(ch.payment_type === "Credit"
                  ? `${ch.name}: ${days} credit days — filled in for you`
                  : `${ch.name}: ${ch.payment_type || "payment terms"} — no credit days`);
              }}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="customer-channel-select"><SelectValue placeholder="Select channel" /></SelectTrigger>
                <SelectContent>{channels.filter(ch => (ch.status || "active") !== "inactive").map(ch => <SelectItem key={ch._id} value={ch._id}>{ch.name}</SelectItem>)}</SelectContent>
              </Select>
              {!form.channel_id && <p className="text-[10px] text-red-600 mt-1" data-testid="customer-channel-required">A customer cannot be saved without a sales channel.</p>}
            </div>
            {editingCustomer && (
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Qcom Super Stockist Sections */}
            {editingCustomer && form.type === "Qcom Super Stockist" && (
              <>
                {/* FOLLOW-UP 4 FIX-UP Q2(b): the names this SS trades under on the platforms */}
                <div className="border border-zinc-200 rounded-lg p-3 mt-3" data-testid="ss-platform-vendor-names">
                  <Label className="text-xs font-semibold text-zinc-700 mb-1 block">Platform vendor names</Label>
                  <p className="text-[10px] text-zinc-400 mb-2">The exact vendor name Blinkit and the other platforms print on this Super Stockist's POs. Separate several names with a comma — ORBIT matches these before anything else.</p>
                  <Input value={(form.platform_vendor_names || []).join(", ")}
                    onChange={(e) => setForm({ ...form, platform_vendor_names: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    placeholder="e.g. KRISHNA MARKETING- BENGALURU"
                    className="rounded-sm" data-testid="customer-platform-vendor-names-input" />
                </div>

                {/* Section A: Platform Onboarding Status */}
                <div className="border border-zinc-200 rounded-lg p-3 mt-3" data-testid="ss-onboarding-section">
                  <Label className="text-xs font-semibold text-zinc-700 mb-2 block">Platform Onboarding Status</Label>
                  <div className="space-y-2">
                    {PLATFORMS.filter(p => p !== "Other").map(platform => {
                      const record = ssOnboarding.find(r => r.platform === platform);
                      const currentStatus = record?.status || "Not Started";
                      return (
                        <div key={platform} className="flex items-center justify-between py-1" data-testid={`onboarding-${platform.toLowerCase().replace(/\s+/g, '-')}`}>
                          <span className="text-xs font-medium text-zinc-700">{platform}</span>
                          <Select value={currentStatus} onValueChange={(v) => {
                            const updated = [...ssOnboarding.filter(r => r.platform !== platform), { platform, status: v }];
                            setSsOnboarding(updated);
                            api.put(`/ss-inventory/onboarding/${editingCustomer._id}`, { platforms: [{ platform, status: v }] }).catch(err => console.error("CustomersPage error:", err));
                          }}>
                            <SelectTrigger className="w-44 h-7 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{ONBOARDING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Section B: SS Inventory Summary */}
                <div className="border border-zinc-200 rounded-lg p-3 mt-3" data-testid="ss-inventory-summary">
                  <Label className="text-xs font-semibold text-zinc-700 mb-2 block">SS Inventory Summary</Label>
                  {ssStock.length === 0 ? (
                    <p className="text-xs text-zinc-400 text-center py-2">No stock data. Stock will appear when orders are delivered to this SS.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-zinc-200"><th className="text-left py-1 px-2 text-[10px] text-zinc-400">SKU</th><th className="text-right py-1 px-2 text-[10px] text-zinc-400">Balance</th><th className="text-left py-1 px-2 text-[10px] text-zinc-400">Status</th></tr></thead>
                        <tbody>
                          {ssStock.map(s => (
                            <tr key={s.sku_id} className="border-b border-zinc-50">
                              <td className="py-1 px-2">{s.sku_name}</td>
                              <td className="py-1 px-2 text-right font-mono font-semibold">{s.current_balance}</td>
                              <td className="py-1 px-2"><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${s.status === 'In Stock' ? 'bg-green-100 text-green-800' : s.status === 'Low Stock' ? 'bg-amber-100 text-amber-800' : s.status === 'Out of Stock' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>{s.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section C: Auto-Reorder Settings */}
                <div className="border border-zinc-200 rounded-lg p-3 mt-3" data-testid="ss-reorder-settings">
                  <Label className="text-xs font-semibold text-zinc-700 mb-2 block">Auto-Reorder Settings</Label>
                  {!autoReorderGlobalOn && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-2"
                       data-testid="auto-reorder-off-note">
                      Auto-reorder is switched off for all super stockists by Admin.
                    </p>
                  )}
                  {isAdminRole && (
                    <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="auto-reorder-global-row">
                      <input type="checkbox" checked={autoReorderGlobalOn}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setAutoReorderGlobalOn(val);
                          api.put("/ss-inventory/settings/auto-reorder", { enabled: val })
                            .then(() => toast.success(val ? "Auto-reorder switched on for all super stockists" : "Auto-reorder switched off for all super stockists"))
                            .catch(() => { setAutoReorderGlobalOn(!val); toast.error("Could not change the switch"); });
                        }}
                        className="rounded" data-testid="auto-reorder-global-toggle" />
                      <span className="text-xs font-semibold text-zinc-700">Auto-reorder on for all super stockists (Admin)</span>
                    </label>
                  )}
                  <div className={`space-y-3 ${autoReorderGlobalOn ? "" : "opacity-50 pointer-events-none"}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" disabled={!autoReorderGlobalOn}
                        checked={ssReorderSettings.auto_reorder_enabled}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setSsReorderSettings(prev => ({ ...prev, auto_reorder_enabled: val }));
                          api.put(`/ss-inventory/reorder-settings/${editingCustomer._id}`, { auto_reorder_enabled: val, reorder_weeks_threshold: ssReorderSettings.reorder_weeks_threshold }).catch(err => console.error("CustomersPage error:", err));
                        }}
                        className="rounded" data-testid="auto-reorder-toggle" />
                      <span className="text-sm font-medium text-zinc-700">Auto-reorder enabled</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-zinc-600">Reorder when stock &lt;</Label>
                      <Input type="number" min={1} max={12} value={ssReorderSettings.reorder_weeks_threshold}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 2;
                          setSsReorderSettings(prev => ({ ...prev, reorder_weeks_threshold: val }));
                        }}
                        onBlur={() => {
                          api.put(`/ss-inventory/reorder-settings/${editingCustomer._id}`, ssReorderSettings).catch(err => console.error("CustomersPage error:", err));
                        }}
                        className="w-16 h-7 text-xs rounded-sm text-center" data-testid="reorder-weeks-input" />
                      <Label className="text-xs text-zinc-600">weeks of avg throughput</Label>
                    </div>
                    <p className="text-[10px] text-zinc-400">When enabled, the system will auto-create a Draft reorder order when any SKU at this SS drops below the threshold.</p>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-customer-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PI Generation Dialog */}
      <Dialog open={showPIDialog} onOpenChange={setShowPIDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pi-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create Proforma Invoice</DialogTitle></DialogHeader>
          {piCustomer && <p className="text-xs text-zinc-500">For: <span className="font-semibold">{piCustomer.name}</span> {piCustomer.company_name ? `(${piCustomer.company_name})` : ""}</p>}
          <div className="space-y-3">
            {/* Fix 3A: Sales Channel selector */}
            <div>
              <Label className="text-xs">Sales Channel</Label>
              <Select value={piChannel || "none"} onValueChange={v => setPiChannel(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs" data-testid="pi-channel-select"><SelectValue placeholder="Select channel for pricing" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- No channel (use MRP) --</SelectItem>{piChannels.map(ch => <SelectItem key={ch._id} value={ch._id}>{ch.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[9px] text-zinc-400 mt-0.5">Select a channel to auto-fill selling prices. Leave blank to use MRP.</p>
            </div>

            <div>
              <Label className="text-xs font-semibold">SKU Selection</Label>
              {/* Fix 3B: Column headers */}
              <div className="grid grid-cols-12 gap-2 mt-2 mb-1">
                <div className="col-span-5"><span className="text-[9px] font-semibold uppercase text-zinc-400">SKU</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Qty</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Unit Price (INR)</span></div>
                <div className="col-span-2"><span className="text-[9px] font-semibold uppercase text-zinc-400">Disc %</span></div>
                <div className="col-span-1"></div>
              </div>
              {piLines.map((pl, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mt-1 items-end" data-testid={`pi-line-${idx}`}>
                  <div className="col-span-5">
                    <Select value={pl.sku_id || "none"} onValueChange={v => {
                      const d = [...piLines]; d[idx].sku_id = v === "none" ? "" : v;
                      const sku = allSKUs.find(s => s.sku_id === v);
                      if (sku) {
                        d[idx].sku_name = sku.sku_name;
                        // Auto-fill price from channel_access or fallback to MRP
                        let price = sku.mrp || 0;
                        if (piChannel) {
                          const ca = (sku.channel_access || []).find(c => c.channel_id === piChannel && c.enabled);
                          if (ca && ca.selling_price > 0) price = ca.selling_price;
                        }
                        d[idx].selling_price = price;
                      }
                      setPiLines(d);
                    }}>
                      <SelectTrigger className="h-7 text-xs rounded-sm"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">--</SelectItem>{allSKUs.map(s => <SelectItem key={s.sku_id} value={s.sku_id}>{s.sku_name} ({s.sku_id})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" min={1} value={pl.quantity} onChange={e => { const d = [...piLines]; d[idx].quantity = parseInt(e.target.value) || 0; setPiLines(d); }} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" value={pl.selling_price} onChange={e => { const d = [...piLines]; d[idx].selling_price = parseFloat(e.target.value) || 0; setPiLines(d); }} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-2"><Input type="number" step="0.1" value={pl.discount_percent} onChange={e => { const d = [...piLines]; d[idx].discount_percent = parseFloat(e.target.value) || 0; setPiLines(d); }} className="h-7 text-xs rounded-sm text-right" /></div>
                  <div className="col-span-1">
                    {piLines.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => setPiLines(piLines.filter((_, i) => i !== idx))}><X size={12} /></Button>}
                  </div>
                </div>
              ))}
              <Button variant="ghost" className="h-6 text-[10px] mt-1" onClick={() => setPiLines([...piLines, { sku_id: "", sku_name: "", quantity: 1, selling_price: 0, discount_percent: 0 }])}>+ Add SKU</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Payment Terms</Label><Input value={piTerms} onChange={e => setPiTerms(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
              <div><Label className="text-xs">Notes</Label><Input value={piNotes} onChange={e => setPiNotes(e.target.value)} className="mt-1 rounded-sm text-xs" placeholder="Special notes" /></div>
            </div>

            {/* Fix 3C: Previous PIs with proper amount formatting */}
            {piHistory.length > 0 && (
              <div className="border border-zinc-200 rounded-sm">
                <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-400">Previous PIs for this Customer</div>
                {piHistory.map((pi, i) => (
                  <div key={i} className="px-3 py-2 border-b border-zinc-50 flex items-center justify-between text-xs">
                    <div><span className="font-mono font-semibold">{pi.pi_number}</span> <span className="text-zinc-400 ml-2">{pi.pi_date}</span></div>
                    <div><span className="font-mono">₹{new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(pi.grand_total || 0)}</span> <span className="text-zinc-400 ml-2">by {pi.generated_by_name}</span></div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPIDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={generatePI} disabled={piGenerating} className="rounded-sm text-xs" data-testid="generate-pi-btn">
              <FileText size={12} /> {piGenerating ? "Generating..." : "Generate PI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
